import { Caption1, makeStyles, mergeClasses, Tab, TabList, tokens } from "@fluentui/react-components";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft16Regular } from "@fluentui/react-icons";
import { useEffect, useMemo, useState, type KeyboardEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ConfirmDialog } from "@/components/app-dialog";
import { useDataTableStyles } from "@/components/data-table";
import { FuiButton } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { canWorkspace, recordWorkspaceAudit, useWorkspaceRole } from "@/features/access/workspace-access";
import { DIRECTORY_SELF_ID } from "@/features/directory/directory";
import { removeDirectoryMember } from "@/features/directory/directory-store";
import { useDirectoryPeople } from "@/features/directory/directory-view";
import { MemberProfileHeader } from "@/features/directory/MemberProfileHeader";
import {
  gatherMemberProfileData,
  getMockProfileFullData,
  profileScale,
  type MemberProfileFullData,
} from "@/features/directory/member-profile-model";
import type { MemberScenario } from "@/features/directory/member-analytics";
import { useFileEntries } from "@/features/files/use-file-entries";
import { ResourceFormDialog } from "@/features/resources/ResourceDialogs";
import { todayISO } from "@/engine/date";
import { fmtPercent } from "@/lib/format";
import { contentShell, shellGutter } from "@/shell/content-frame";
import { UserWeeklyPanel } from "@/features/user-weekly/UserWeeklyPanel";
import { usePageAction } from "@/shell/page-action";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";

const useStyles = makeStyles({
  page: {
    ...contentShell,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  head: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: "4px",
    flexShrink: 0,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: shellGutter,
    paddingRight: shellGutter,
  },
  back: {
    alignSelf: "flex-start",
    marginLeft: `calc(-1 * ${tokens.spacingHorizontalS})`,
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightRegular,
    ":hover": {
      color: tokens.colorNeutralForeground1,
    },
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalS} ${shellGutter} ${tokens.spacingVerticalXXL}`,
    boxSizing: "border-box",
  },
  quiet: {
    color: tokens.colorNeutralForeground3,
  },
  row: {
    cursor: "pointer",
    ":hover td": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":active td": { backgroundColor: tokens.colorNeutralBackground1Pressed },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "-2px",
    },
  },
  titleCell: {
    maxWidth: 0,
    width: "100%",
    minWidth: "240px",
    height: "auto",
  },
  overdue: {
    color: tokens.colorPaletteRedForeground1,
  },
  scale: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
  },
  roleCell: {
    maxWidth: "280px",
  },
});

function activateRow(event: KeyboardEvent<HTMLTableRowElement>, open: () => void) {
  if (event.key !== "Enter" && event.key !== " ") return;
  event.preventDefault();
  open();
}

export function MemberProfilePage() {
  const styles = useStyles();
  const table = useDataTableStyles();
  const { personId = "" } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const section = params.get("tab") === "weekly" ? "weekly" : "plans";

  const people = useDirectoryPeople();
  const person = people.find((row) => row.id === personId);
  const workspaceRole = useWorkspaceRole();
  const canManage = canWorkspace(workspaceRole, "member.invite");

  const resource = useCatalogStore((s) => s.resources[personId]);
  const teams = useCatalogStore((s) => s.teams);
  const projects = useCatalogStore((s) => s.projects);
  const allResources = useCatalogStore((s) => s.resources);
  const activeId = useProjectStore((s) => s.activeId);
  const openProject = useProjectStore((s) => s.project);
  const updateResource = useCatalogStore((s) => s.updateResource);
  const allFiles = useFileEntries();

  const [editOpen, setEditOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [scenario, setScenario] = useState<MemberScenario>("default");
  const today = todayISO();

  useEffect(() => {
    setScenario("default");
  }, [personId]);

  const rawData: MemberProfileFullData = useMemo(() => {
    return gatherMemberProfileData(projects, allFiles, person?.id ?? "", person?.name ?? "", today);
  }, [projects, allFiles, person?.id, person?.name, today]);

  const data: MemberProfileFullData = useMemo(() => {
    return getMockProfileFullData(scenario, rawData, today);
  }, [scenario, rawData, today]);

  const planRatios = useMemo(() => {
    const ratios = new Map<string, number>();
    for (const plan of data.plans) {
      const assignment = projects[plan.id]?.resourceAssignments?.find((row) => row.resourceId === personId);
      if (assignment) {
        ratios.set(`${plan.id}-${plan.role}`, assignment.allocationRatio);
        continue;
      }
      const strip = data.analytics.projectStrips.find((row) => row.projectId === plan.id);
      if (strip) ratios.set(`${plan.id}-${plan.role}`, strip.allocationRatio);
    }
    return ratios;
  }, [data.plans, data.analytics.projectStrips, projects, personId]);

  const scale = profileScale(data.stats);

  usePageAction(null);

  const openPlan = (projectId: string) => {
    useProjectStore.getState().openProject(projectId);
    navigate(`/projects/${projectId}/schedule`);
  };

  if (!person) {
    return (
      <div className={styles.page}>
        <div className={styles.head}>
          <FuiButton className={styles.back} appearance="subtle" icon={<ChevronLeft16Regular />} onClick={() => navigate("/members")}>
            멤버 목록
          </FuiButton>
        </div>
        <GhostEmptyState title="멤버를 찾을 수 없습니다" description="목록에 없는 사람입니다." />
      </div>
    );
  }

  const locked = person.id === DIRECTORY_SELF_ID || person.grant.role === "owner" || !canManage;

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <FuiButton className={styles.back} appearance="subtle" icon={<ChevronLeft16Regular />} onClick={() => navigate("/members")}>
          멤버 목록
        </FuiButton>
        <MemberProfileHeader
          person={person}
          canEdit={!!resource}
          locked={locked}
          scenario={scenario}
          onEdit={() => setEditOpen(true)}
          onRemove={() => setRemoveOpen(true)}
          onScenarioChange={setScenario}
        />
        {scale ? <span className={styles.scale}>{scale}</span> : null}
        <TabList
          size="small"
          selectedValue={section}
          aria-label="멤버"
          onTabSelect={(_, data) => {
            const next = new URLSearchParams(params);
            if (data.value === "weekly") next.set("tab", "weekly");
            else next.delete("tab");
            setParams(next, { replace: true });
          }}
        >
          <Tab value="plans">참여</Tab>
          <Tab value="weekly">주간 보고</Tab>
        </TabList>
      </div>

      <div className={styles.body}>
        {section === "weekly" ? (
          <UserWeeklyPanel
            embedded
            title="주간 보고"
            actor={{ name: person.name, resourceId: person.id }}
            projects={projects}
            live={activeId && openProject.id === activeId ? openProject : null}
            resources={allResources}
          />
        ) : data.plans.length === 0 ? <Caption1 className={styles.quiet}>맡은 플랜이 없습니다.</Caption1> : null}
        {section === "plans" && data.plans.length > 0 ? (
              <div className={table.frame}>
                <table className={table.table} aria-label="참여 플랜">
                  <thead>
                    <tr>
                      <th scope="col" className={table.th}>플랜</th>
                      <th scope="col" className={table.th}>역할</th>
                      <th scope="col" className={mergeClasses(table.th, table.num)}>가동률</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.plans.map((plan) => {
                      const allocation = planRatios.get(`${plan.id}-${plan.role}`);
                      const over = allocation !== undefined && allocation > 1.05;
                      const open = () => openPlan(plan.id);
                      return (
                        <tr
                          key={`${plan.id}-${plan.role}`}
                          className={styles.row}
                          tabIndex={0}
                          onClick={open}
                          onKeyDown={(event) => activateRow(event, open)}
                          aria-label={`${plan.name} ${plan.role} 열기`}
                        >
                          <td className={mergeClasses(table.td, styles.titleCell)} title={plan.name}>
                            <span className={table.clip}>{plan.name}</span>
                          </td>
                          <td className={mergeClasses(table.td, styles.roleCell)} title={plan.role || undefined}>
                            <span className={table.clip}>{plan.role || "—"}</span>
                          </td>
                          <td className={mergeClasses(table.td, table.num, over && styles.overdue)} title={over ? "과부하" : undefined}>
                            {allocation !== undefined ? fmtPercent(allocation * 100) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
        ) : null}
      </div>

      {editOpen && resource ? (
        <ResourceFormDialog
          open
          initial={resource}
          teams={Object.values(teams)}
          onClose={() => setEditOpen(false)}
          onSave={(formData) => {
            updateResource(resource.id, formData);
            setEditOpen(false);
          }}
        />
      ) : null}

      <ConfirmDialog
        open={removeOpen}
        title="워크스페이스에서 제거"
        message="워크스페이스에서 뺄까요? 인력 배정은 플랜에 남습니다."
        confirmLabel="제거"
        onCancel={() => setRemoveOpen(false)}
        onConfirm={() => {
          if (removeDirectoryMember(person.id)) {
            recordWorkspaceAudit("멤버 제거", `${person.name}을 워크스페이스에서 뺐습니다.`);
            navigate("/members");
          }
          setRemoveOpen(false);
        }}
      />
    </div>
  );
}
